import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { FileDto, ScheduledMailDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { Empty, ErrorBanner, Field, Modal, PasswordInput } from '../ui';
import { ask } from '../dialogs';
import { SchedulePicker, newWhen, whenError, whenLabel, whenToIso, type When } from '../schedule';

export interface Address {
  name: string;
  address: string;
}
export interface Folder {
  path: string;
  name: string;
  kind: 'inbox' | 'sent' | 'drafts' | 'trash' | 'junk' | 'other';
  unread: number;
}
export interface Summary {
  uid: number;
  from: Address;
  to: Address[];
  subject: string;
  date: string | null;
  seen: boolean;
  hasAttachments: boolean;
}
interface Attachment {
  index: number;
  filename: string;
  contentType: string;
  size: number;
}
interface Message extends Summary {
  cc: Address[];
  html: string | null;
  text: string;
  remoteImagesBlocked: boolean;
  messageId: string | null;
  references: string[];
  attachments: Attachment[];
}
type AccountInfo = { configured: false } | { configured: true; email: string; username: string; imapHost: string; smtpHost: string };

const FOLDER_LABEL: Record<Folder['kind'], string> = {
  inbox: 'Ricevuti',
  sent: 'Inviati',
  drafts: 'Bozze',
  trash: 'Cestino',
  junk: 'Spam',
  other: '',
};

/** Cartella finta per le email programmate (non esiste sulla casella). */
const SCHED = '__scheduled';

const who = (a: Address) => a.name || a.address || '(sconosciuto)';

function shortDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  return d.toLocaleDateString('it-IT');
}

function fullDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'full', timeStyle: 'short' }) : '';
}

function size(n: number): string {
  if (n > 1_000_000) return `${(n / 1_000_000).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1000))} KB`;
}

interface Draft {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  text: string;
  inReplyTo?: string;
  references?: string[];
  /** Documenti del CRM (questionari, modelli) da allegare: li aggiunge il server. */
  files?: FileDto[];
}

/** Client di posta del commerciale: legge e invia dalla sua casella (Aruba) tramite il server. */
export function MailPage({ ctx, onUnread }: { ctx: Ctx; onUnread: (n: number) => void }) {
  const { api } = ctx;
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [folder, setFolder] = useState('INBOX');
  const [messages, setMessages] = useState<Summary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [current, setCurrent] = useState<Message | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [pane, setPane] = useState<'folders' | 'list' | 'read'>('list');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [compose, setCompose] = useState<Draft | null>(null);
  const [setup, setSetup] = useState(false);
  const [scheduled, setScheduled] = useState<ScheduledMailDto[]>([]);
  const [schedSel, setSchedSel] = useState<number | null>(null);
  const folderRef = useRef(folder);
  folderRef.current = folder;

  const loadAccount = useCallback(async () => {
    try {
      setAccount(await api.get<AccountInfo>('/api/mail/account'));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api]);

  const loadFolders = useCallback(async () => {
    const f = await api.get<Folder[]>('/api/mail/folders');
    setFolders(f);
    const inbox = f.find((x) => x.kind === 'inbox');
    if (inbox) onUnread(inbox.unread);
  }, [api, onUnread]);

  const loadScheduled = useCallback(async () => {
    try {
      setScheduled(await api.get<ScheduledMailDto[]>('/api/mail/scheduled'));
    } catch {
      // la lista delle programmate non blocca la posta
    }
  }, [api]);

  const loadList = useCallback(
    async (silent = false) => {
      if (folderRef.current === SCHED) return;
      if (!silent) setLoading(true);
      try {
        const qs = `folder=${encodeURIComponent(folderRef.current)}&page=${page}${query ? `&search=${encodeURIComponent(query)}` : ''}`;
        const r = await api.get<{ total: number; messages: Summary[] }>(`/api/mail/messages?${qs}`);
        setMessages(r.messages);
        setTotal(r.total);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [api, page, query],
  );

  useEffect(() => {
    void loadAccount();
  }, [loadAccount]);

  // Arrivo da Documenti ("Invia per mail"): apre subito un nuovo messaggio con il file già allegato
  useEffect(() => {
    const p = (ctx.pageParam as { compose?: Partial<Draft> } | null)?.compose;
    if (p) setCompose({ to: '', cc: '', bcc: '', subject: '', text: '', ...p });
  }, [ctx.pageParam]);

  // Arrivo dalla ricerca o dalla scheda azienda: cerca subito e, se indicato, apre quel messaggio
  const pendingOpen = useRef<number | null>(null);
  useEffect(() => {
    const p = ctx.pageParam as { search?: string; folder?: string; openUid?: number } | null;
    if (!p?.search) return;
    setFolder(p.folder ?? 'INBOX');
    setSearch(p.search);
    setQuery(p.search);
    setPage(1);
    pendingOpen.current = p.openUid ?? null;
  }, [ctx.pageParam]);

  const configured = account?.configured === true;

  useEffect(() => {
    if (!configured) return;
    void loadFolders().catch((e) => setError((e as Error).message));
    void loadScheduled();
  }, [configured, loadFolders, loadScheduled]);

  useEffect(() => {
    if (!configured) return;
    void loadList();
  }, [configured, folder, page, query, loadList]);

  // Aggiornamento automatico ogni minuto.
  useEffect(() => {
    if (!configured) return;
    const t = setInterval(() => {
      void loadList(true);
      void loadFolders().catch(() => undefined);
      void loadScheduled();
    }, 60_000);
    return () => clearInterval(t);
  }, [configured, loadList, loadFolders, loadScheduled]);

  useEffect(() => {
    if (pendingOpen.current == null) return;
    const m = messages.find((x) => x.uid === pendingOpen.current);
    if (m) {
      pendingOpen.current = null;
      void open(m);
    }
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (m: Summary) => {
    setSelected(m.uid);
    setPane('read');
    setCurrent(null);
    try {
      const full = await api.get<Message>(`/api/mail/messages/${m.uid}?folder=${encodeURIComponent(folder)}`);
      setCurrent(full);
      if (!m.seen) {
        setMessages((list) => list.map((x) => (x.uid === m.uid ? { ...x, seen: true } : x)));
        void loadFolders().catch(() => undefined);
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const remove = async () => {
    if (!current) return;
    const inTrash = folders.find((f) => f.path === folder)?.kind === 'trash';
    if (inTrash && !(await ask('Eliminare definitivamente questo messaggio?'))) return;
    try {
      await api.del(`/api/mail/messages/${current.uid}?folder=${encodeURIComponent(folder)}`);
      setMessages((list) => list.filter((x) => x.uid !== current.uid));
      setCurrent(null);
      setSelected(null);
      setPane('list');
      void loadFolders().catch(() => undefined);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const markUnread = async () => {
    if (!current) return;
    try {
      await api.post(`/api/mail/messages/${current.uid}/seen`, { folder, seen: false });
      setMessages((list) => list.map((x) => (x.uid === current.uid ? { ...x, seen: false } : x)));
      setCurrent(null);
      setSelected(null);
      setPane('list');
      void loadFolders().catch(() => undefined);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const download = async (a: Attachment) => {
    if (!current) return;
    try {
      const blob = await api.download(`/api/mail/messages/${current.uid}/attachments/${a.index}?folder=${encodeURIComponent(folder)}`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const reply = (all: boolean) => {
    if (!current) return;
    const me = account?.configured ? account.email.toLowerCase() : '';
    const others = all ? [...current.to, ...current.cc].map((a) => a.address).filter((a) => a && a.toLowerCase() !== me) : [];
    const quoted = current.text
      .split('\n')
      .map((l) => `> ${l}`)
      .join('\n');
    setCompose({
      to: [current.from.address, ...others.filter((a) => a !== current.from.address)].join(', '),
      cc: '',
      bcc: '',
      subject: /^re:/i.test(current.subject) ? current.subject : `Re: ${current.subject}`,
      text: `\n\nIl ${fullDate(current.date)}, ${who(current.from)} ha scritto:\n${quoted}`,
      inReplyTo: current.messageId ?? undefined,
      references: [...current.references, ...(current.messageId ? [current.messageId] : [])],
    });
  };

  const forward = () => {
    if (!current) return;
    setCompose({
      to: '',
      cc: '',
      bcc: '',
      subject: /^(fwd?|i):/i.test(current.subject) ? current.subject : `Inoltro: ${current.subject}`,
      text: `\n\n---------- Messaggio inoltrato ----------\nDa: ${who(current.from)} <${current.from.address}>\nData: ${fullDate(current.date)}\nOggetto: ${current.subject}\n\n${current.text}`,
    });
  };

  const pickFolder = (path: string) => {
    setFolder(path);
    setPage(1);
    setQuery('');
    setSearch('');
    setCurrent(null);
    setSelected(null);
    setSchedSel(null);
    setPane('list');
    if (path === SCHED) void loadScheduled();
  };

  // ------------------------------------------------------------------ schermate

  if (account === null) {
    return <div className="panel"><div className="muted small">Caricamento…</div></div>;
  }

  if (!configured || setup) {
    return (
      <div className="panel mail-setup">
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <AccountForm
          ctx={ctx}
          current={account.configured ? account : null}
          onDone={() => {
            setSetup(false);
            setError(null);
            void loadAccount();
          }}
          onCancel={configured ? () => setSetup(false) : undefined}
        />
      </div>
    );
  }

  const current_folder = folders.find((f) => f.path === folder);
  const inSched = folder === SCHED;
  const schedItem = scheduled.find((x) => x.id === schedSel) ?? null;
  const pages = Math.max(1, Math.ceil(total / 30));

  return (
    <div className="mail" data-pane={pane}>
      <aside className="mail-folders">
        <button type="button" className="btn dark" onClick={() => setCompose({ to: '', cc: '', bcc: '', subject: '', text: '' })}>
          <Icon name="edit" size={16} /> Scrivi
        </button>
        <nav>
          {(folders.length ? folders : [{ path: 'INBOX', name: 'INBOX', kind: 'inbox' as const, unread: 0 }]).map((f) => (
            <button key={f.path} type="button" className={`mail-folder${f.path === folder ? ' active' : ''}`} onClick={() => pickFolder(f.path)}>
              <span>{FOLDER_LABEL[f.kind] || f.name}</span>
              {f.unread > 0 && <b>{f.unread}</b>}
            </button>
          ))}
          <button type="button" className={`mail-folder${inSched ? ' active' : ''}`} onClick={() => pickFolder(SCHED)}>
            <span className="mail-folder-sched">
              <Icon name="clock" size={13} /> Programmate
            </span>
            {scheduled.length > 0 && <b className={scheduled.some((x) => x.status === 'failed') ? 'bad' : ''}>{scheduled.length}</b>}
          </button>
        </nav>
        <span className="grow" />
        <div className="muted small mail-account">{account.configured ? account.email : ''}</div>
        <button type="button" className="btn small ghost" title="Le impostazioni della tua casella di posta" onClick={() => setSetup(true)}>
          <Icon name="gear" size={14} /> Casella
        </button>
      </aside>

      <section className="mail-list">
        <div className="mail-list-head">
          <button type="button" className="btn icon ghost mail-back" onClick={() => setPane('folders')} title="Cartelle">
            <Icon name="chevronLeft" size={18} />
          </button>
          <h2>{inSched ? 'Programmate' : current_folder ? FOLDER_LABEL[current_folder.kind] || current_folder.name : 'Ricevuti'}</h2>
          <button type="button" className="btn icon ghost" onClick={() => { void loadList(); void loadFolders().catch(() => undefined); void loadScheduled(); }} title="Aggiorna">
            <Icon name="refresh" size={16} />
          </button>
          <button type="button" className="btn icon dark mail-compose-fab" onClick={() => setCompose({ to: '', cc: '', bcc: '', subject: '', text: '' })} title="Scrivi">
            <Icon name="edit" size={16} />
          </button>
        </div>
        {!inSched && (
        <form
          className="mail-search"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setQuery(search.trim());
          }}
        >
          <Icon name="search" size={16} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca nella cartella" />
          {query && (
            <button type="button" className="btn icon small ghost" title="Cancella la ricerca" onClick={() => { setSearch(''); setQuery(''); setPage(1); }}>
              <Icon name="x" size={14} />
            </button>
          )}
        </form>
        )}
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {inSched ? (
          <div className="mail-items">
            {scheduled.length === 0 && <Empty icon="clock">Nessuna email programmata.</Empty>}
            {scheduled.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`mail-item${schedSel === m.id ? ' active' : ''}`}
                onClick={() => {
                  setSchedSel(m.id);
                  setPane('read');
                }}
              >
                <div className="mail-item-top">
                  <span className="mail-from">A: {m.to}</span>
                  <span className={`mail-date${m.status === 'failed' ? ' bad-text' : ''}`}>{m.status === 'failed' ? 'Non inviata' : whenLabel(m.sendAt)}</span>
                </div>
                <div className="mail-subject">
                  {m.kind !== 'mail' && <Icon name="paperclip" size={13} />}
                  {m.subject || '(senza oggetto)'}
                </div>
              </button>
            ))}
          </div>
        ) : (
        <div className="mail-items">
          {loading && messages.length === 0 && <div className="muted small" style={{ padding: 14 }}>Caricamento…</div>}
          {!loading && messages.length === 0 && <Empty icon="inbox">Nessun messaggio.</Empty>}
          {messages.map((m) => {
            const outgoing = current_folder?.kind === 'sent' || current_folder?.kind === 'drafts';
            return (
              <button key={m.uid} type="button" className={`mail-item${m.seen ? '' : ' unread'}${selected === m.uid ? ' active' : ''}`} onClick={() => void open(m)}>
                <div className="mail-item-top">
                  <span className="mail-from">{outgoing ? `A: ${m.to.map(who).join(', ') || '—'}` : who(m.from)}</span>
                  <span className="mail-date">{shortDate(m.date)}</span>
                </div>
                <div className="mail-subject">
                  {m.hasAttachments && <Icon name="paperclip" size={13} />}
                  {m.subject || '(senza oggetto)'}
                </div>
              </button>
            );
          })}
        </div>
        )}
        {!inSched && pages > 1 && (
          <div className="mail-pager">
            <button type="button" className="btn small" disabled={page <= 1} title="Pagina precedente" onClick={() => setPage((p) => p - 1)}>
              <Icon name="chevronLeft" size={14} />
            </button>
            <span className="muted small">
              {page} / {pages}
            </span>
            <button type="button" className="btn small" disabled={page >= pages} title="Pagina successiva" onClick={() => setPage((p) => p + 1)}>
              <Icon name="chevronRight" size={14} />
            </button>
          </div>
        )}
      </section>

      <section className="mail-read">
        {inSched ? (
          schedItem ? (
            <ScheduledDetail
              key={schedItem.id}
              ctx={ctx}
              item={schedItem}
              onBack={() => setPane('list')}
              onChanged={(gone) => {
                if (gone) setSchedSel(null);
                void loadScheduled();
              }}
            />
          ) : (
            <Empty icon="clock">Seleziona un'email programmata.</Empty>
          )
        ) : !selected ? (
          <Empty icon="mail">Seleziona un messaggio.</Empty>
        ) : !current ? (
          <div className="muted small" style={{ padding: 20 }}>Caricamento…</div>
        ) : (
          <>
            <div className="mail-read-head">
              <button type="button" className="btn icon ghost mail-back" onClick={() => setPane('list')} title="Elenco">
                <Icon name="chevronLeft" size={18} />
              </button>
              <div className="grow" />
              <button type="button" className="btn small" onClick={() => reply(false)}>
                <Icon name="reply" size={14} /> Rispondi
              </button>
              <button type="button" className="btn small" title="Rispondi anche a chi era in copia" onClick={() => reply(true)}>
                Rispondi a tutti
              </button>
              <button type="button" className="btn small" onClick={forward}>
                <Icon name="forward" size={14} /> Inoltra
              </button>
              <button type="button" className="btn small ghost" onClick={() => void markUnread()} title="Segna come non letto">
                <Icon name="mail" size={14} />
              </button>
              <button type="button" className="btn small ghost" onClick={() => void remove()} title={current_folder?.kind === 'trash' ? 'Elimina per sempre' : 'Sposta nel cestino'}>
                <Icon name="trash" size={14} />
              </button>
            </div>
            <div className="mail-read-body">
              <h3>{current.subject || '(senza oggetto)'}</h3>
              <div className="mail-meta">
                <div>
                  <b>{who(current.from)}</b> <span className="muted">&lt;{current.from.address}&gt;</span>
                </div>
                <div className="muted small">A: {current.to.map((a) => a.address || a.name).join(', ')}</div>
                {current.cc.length > 0 && <div className="muted small">Cc: {current.cc.map((a) => a.address || a.name).join(', ')}</div>}
                <div className="muted small">{fullDate(current.date)}</div>
              </div>
              {current.remoteImagesBlocked && <div className="mail-note">Le immagini esterne sono bloccate per proteggere la tua privacy.</div>}
              {current.attachments.length > 0 && (
                <div className="mail-atts">
                  {current.attachments.map((a) => (
                    <button key={a.index} type="button" className="btn small" onClick={() => void download(a)}>
                      <Icon name="paperclip" size={13} /> {a.filename} <span className="muted">{size(a.size)}</span>
                    </button>
                  ))}
                </div>
              )}
              {current.html ? (
                <div
                  className="mail-body"
                  dangerouslySetInnerHTML={{ __html: current.html }}
                  onClick={(e) => {
                    const a = (e.target as HTMLElement).closest('a');
                    if (a) {
                      e.preventDefault();
                      const href = a.getAttribute('href');
                      if (href) window.open(href, '_blank', 'noopener');
                    }
                  }}
                />
              ) : (
                <pre className="mail-body plain">{current.text}</pre>
              )}
            </div>
          </>
        )}
      </section>

      {compose && (
        <Composer
          ctx={ctx}
          draft={compose}
          onClose={() => setCompose(null)}
          onSent={() => {
            setCompose(null);
            void loadList(true);
            void loadFolders().catch(() => undefined);
            void loadScheduled();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function AccountForm({
  ctx,
  current,
  onDone,
  onCancel,
}: {
  ctx: Ctx;
  current: { email: string; username: string; imapHost: string; smtpHost: string } | null;
  onDone: () => void;
  onCancel?: () => void;
}) {
  const [email, setEmail] = useState(current?.email ?? ctx.me.user.email);
  const [password, setPassword] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [imapHost, setImapHost] = useState(current?.imapHost ?? 'imaps.aruba.it');
  const [smtpHost, setSmtpHost] = useState(current?.smtpHost ?? 'smtps.aruba.it');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await ctx.api.put('/api/mail/account', { email: email.trim(), password, imapHost: imapHost.trim(), smtpHost: smtpHost.trim() });
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!(await ask('Scollegare la casella? La password salvata sul server viene eliminata (i messaggi restano su Aruba).'))) return;
    try {
      await ctx.api.del('/api/mail/account');
      onDone();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <form onSubmit={save} className="mail-setup-form">
      <h2>{current ? 'Casella di posta' : 'Collega la tua casella'}</h2>
      <p className="muted" title="La password viene cifrata sul server e non compare mai nell’app.">
        Email e password della tua casella Aruba: la password resta <b>protetta sul server</b>.
      </p>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <Field label="Email">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" />
      </Field>
      <Field label="Password della casella" hint={current ? 'Reinseriscila per salvare modifiche.' : undefined}>
        <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="off" />
      </Field>
      <button type="button" className="btn small ghost" onClick={() => setAdvanced((v) => !v)}>
        {advanced ? 'Nascondi impostazioni server' : 'Impostazioni server'}
      </button>
      {advanced && (
        <div className="fields-2" style={{ marginTop: 10 }}>
          <Field label="Server per ricevere (IMAP)">
            <input value={imapHost} onChange={(e) => setImapHost(e.target.value)} />
          </Field>
          <Field label="Server per inviare (SMTP)">
            <input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} />
          </Field>
        </div>
      )}
      <div className="row-actions">
        <button className="btn dark" type="submit" disabled={busy}>
          {busy ? 'Verifica in corso…' : 'Collega e verifica'}
        </button>
        {onCancel && (
          <button className="btn" type="button" onClick={onCancel}>
            Annulla
          </button>
        )}
        {current && (
          <button className="btn danger" type="button" onClick={() => void disconnect()}>
            Scollega
          </button>
        )}
      </div>
    </form>
  );
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

function Composer({ ctx, draft, onClose, onSent }: { ctx: Ctx; draft: Draft; onClose: () => void; onSent: () => void }) {
  const [d, setD] = useState(draft);
  const [showCc, setShowCc] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [crm, setCrm] = useState<FileDto[]>(draft.files ?? []);
  const [shared, setShared] = useState<FileDto[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [when, setWhen] = useState<When | null>(null);
  const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setD({ ...d, [k]: e.target.value });
  const split = (s: string) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
  const total = files.reduce((n, f) => n + f.size, 0) + crm.reduce((n, f) => n + f.size, 0);

  // Questionari e modelli della sezione Documenti: si allegano senza scaricarli
  useEffect(() => {
    ctx.api.get<FileDto[]>('/api/files/shared').then(setShared).catch(() => undefined);
  }, [ctx.api]);
  const pickable = shared.filter((f) => !crm.some((x) => x.id === f.id));

  const send = async () => {
    const to = split(d.to);
    if (!to.length) return setError('Inserisci almeno un destinatario');
    if (total > 19_000_000) return setError('Allegati troppo grandi (massimo 19 MB)');
    if (when) {
      const bad = whenError(when);
      if (bad) return setError(bad);
    }
    setBusy(true);
    setError(null);
    try {
      const attachments = await Promise.all(files.map(async (f) => ({ filename: f.name, contentType: f.type || undefined, contentBase64: await toBase64(f) })));
      await ctx.api.post('/api/mail/send', {
        to,
        cc: split(d.cc),
        bcc: split(d.bcc),
        subject: d.subject,
        text: d.text,
        inReplyTo: d.inReplyTo,
        references: d.references,
        attachments,
        fileIds: crm.map((f) => f.id),
        sendAt: whenToIso(when) ?? undefined,
      });
      onSent();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Nuovo messaggio"
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          {!when && (
            <button className="btn" type="button" onClick={() => setWhen(newWhen())} disabled={busy} title="Scegli giorno e ora dell'invio">
              <Icon name="clock" size={15} /> Programma
            </button>
          )}
          <button className="btn dark" type="button" onClick={() => void send()} disabled={busy}>
            <Icon name={when ? 'clock' : 'send'} size={15} /> {busy ? (when ? 'Salvataggio…' : 'Invio…') : when ? 'Programma invio' : 'Invia'}
          </button>
        </>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <Field label="A">
        <input value={d.to} onChange={set('to')} placeholder="destinatario@esempio.it" autoFocus={!d.to} />
      </Field>
      {!showCc ? (
        <button type="button" className="btn small ghost" style={{ marginBottom: 10 }} title="Altri destinatari in copia (Ccn: in copia nascosta, gli altri non li vedono)" onClick={() => setShowCc(true)}>
          Cc / Ccn
        </button>
      ) : (
        <div className="fields-2">
          <Field label="Cc">
            <input value={d.cc} onChange={set('cc')} />
          </Field>
          <Field label="Ccn">
            <input value={d.bcc} onChange={set('bcc')} />
          </Field>
        </div>
      )}
      <Field label="Oggetto">
        <input value={d.subject} onChange={set('subject')} />
      </Field>
      <Field label="Messaggio">
        <textarea value={d.text} onChange={set('text')} rows={12} style={{ minHeight: 220 }} autoFocus={!!d.to} />
      </Field>
      <div className="mail-atts">
        <label className="btn small">
          <Icon name="paperclip" size={13} /> Allega file
          <input
            type="file"
            multiple
            hidden
            onChange={(e) => {
              setFiles((f) => [...f, ...Array.from(e.target.files ?? [])]);
              e.target.value = '';
            }}
          />
        </label>
        {pickable.length > 0 && (
          <select
            className="mail-crm"
            value=""
            title="Questionari e modelli dalla sezione Documenti"
            onChange={(e) => {
              const f = shared.find((x) => x.id === Number(e.target.value));
              if (f) setCrm((l) => [...l, f]);
            }}
          >
            <option value="">Allega dal CRM: questionari e modelli…</option>
            {pickable.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        )}
        {crm.map((f) => (
          <span key={`crm${f.id}`} className="pill-file" title="Dalla sezione Documenti">
            <Icon name="folder" size={12} /> {f.name} <span className="muted">{size(f.size)}</span>
            <button type="button" className="btn icon small ghost" title="Togli l’allegato" onClick={() => setCrm((list) => list.filter((x) => x.id !== f.id))}>
              <Icon name="x" size={12} />
            </button>
          </span>
        ))}
        {files.map((f, i) => (
          <span key={i} className="pill-file">
            {f.name} <span className="muted">{size(f.size)}</span>
            <button type="button" className="btn icon small ghost" title="Togli l’allegato" onClick={() => setFiles((list) => list.filter((_, j) => j !== i))}>
              <Icon name="x" size={12} />
            </button>
          </span>
        ))}
      </div>
      {when && <SchedulePicker value={when} onChange={setWhen} onClear={() => setWhen(null)} />}
    </Modal>
  );
}

function ScheduledDetail({ ctx, item, onBack, onChanged }: { ctx: Ctx; item: ScheduledMailDto; onBack: () => void; onChanged: (gone: boolean) => void }) {
  const local = new Date(item.sendAt);
  const pad = (n: number) => String(n).padStart(2, '0');
  const [when, setWhen] = useState<When>({
    date: `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}`,
    time: `${pad(local.getHours())}:${pad(local.getMinutes())}`,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>, gone: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged(gone);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const save = () => {
    const bad = whenError(when);
    if (bad) return setError(bad);
    void run(() => ctx.api.put(`/api/mail/scheduled/${item.id}`, { sendAt: whenToIso(when) }), false);
  };
  const cancel = async () => {
    if (!(await ask(`L'email "${item.subject || '(senza oggetto)'}" non partirà.`, { title: 'Annullare l’invio?', ok: 'Sì, annulla invio', cancel: 'Indietro', danger: true }))) return;
    void run(() => ctx.api.del(`/api/mail/scheduled/${item.id}`), true);
  };
  const kind = item.kind === 'quote' ? 'Preventivo con PDF allegato' : item.kind === 'invoice' ? 'Copia di cortesia della fattura (PDF)' : null;
  return (
    <>
      <div className="mail-read-head">
        <button type="button" className="btn icon ghost mail-back" onClick={onBack} title="Elenco">
          <Icon name="chevronLeft" size={18} />
        </button>
        <div className="grow" />
        <button type="button" className="btn small" disabled={busy} title="Parte subito, senza aspettare l’orario" onClick={() => void run(() => ctx.api.post(`/api/mail/scheduled/${item.id}/now`, {}), true)}>
          <Icon name="send" size={14} /> Invia ora
        </button>
        <button type="button" className="btn small danger" disabled={busy} onClick={() => void cancel()}>
          Annulla invio
        </button>
      </div>
      <div className="mail-read-body">
        <h3>{item.subject || '(senza oggetto)'}</h3>
        <div className="mail-meta">
          <div className="muted small">A: {item.to}</div>
          {kind && <div className="muted small">{kind}</div>}
          <div>{item.status === 'failed' ? <b className="bad-text">Non inviata</b> : <b>Partirà {whenLabel(item.sendAt)}</b>}</div>
        </div>
        {item.status === 'failed' && item.error && <div className="mail-note">{item.error}</div>}
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <div className="muted small" style={{ margin: '14px 0 6px' }}>
          Cambia giorno o ora
        </div>
        <SchedulePicker value={when} onChange={setWhen} />
        <button type="button" className="btn dark" disabled={busy} onClick={save} style={{ marginTop: 10 }}>
          <Icon name="clock" size={15} /> {item.status === 'failed' ? 'Riprogramma' : 'Salva nuovo orario'}
        </button>
      </div>
    </>
  );
}
